import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticationResult,
  AuthError,
  BrowserAuthError,
  PopupRequest,
  PublicClientApplication
} from '@azure/msal-browser';
import { environment } from '../../../environments/environment';

export type MicrosoftLoginAudience = 'learner' | 'staff';

@Injectable({ providedIn: 'root' })
export class MicrosoftAuthService {
  private router = inject(Router);
  private pca: PublicClientApplication | null = null;
  private initPromise: Promise<PublicClientApplication> | null = null;
  private inFlight: Promise<string> | null = null;

  private popupRedirectUri(): string {
    return typeof window !== 'undefined'
      ? `${window.location.origin}/connecting`
      : '/connecting';
  }

  private async ensureInit(): Promise<PublicClientApplication> {
    if (!this.initPromise) {
      this.initPromise = (async () => {
        const pca = new PublicClientApplication({
          auth: {
            clientId: environment.microsoftClientId,
            authority: 'https://login.microsoftonline.com/common',
            redirectUri: this.popupRedirectUri(),
            postLogoutRedirectUri:
              typeof window !== 'undefined' ? window.location.origin : undefined
          },
          cache: {
            cacheLocation: 'sessionStorage'
          }
        });
        await pca.initialize();
        try {
          await pca.handleRedirectPromise();
        } catch {
          await this.purgeMsalState(pca);
        }
        this.pca = pca;
        return pca;
      })();
    }
    return this.initPromise;
  }

  async startLogin(audience: MicrosoftLoginAudience = 'learner'): Promise<void> {
    const idToken = await this.acquireIdTokenInteractive();
    await this.router.navigate(['/connecting'], {
      queryParams: {
        source: 'microsoft',
        credential: idToken,
        ...(audience === 'staff' ? { audience: 'staff' } : {})
      }
    });
  }

  private acquireIdTokenInteractive(): Promise<string> {
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.doAcquireIdTokenPopup().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async doAcquireIdTokenPopup(): Promise<string> {
    const pca = await this.ensureInit();
    await this.purgeMsalState(pca);

    const request: PopupRequest = {
      scopes: ['user.read', 'profile', 'email', 'openid', 'offline_access'],
      redirectUri: this.popupRedirectUri(),
      prompt: 'select_account'
    };

    try {
      return await this.loginPopup(pca, request);
    } catch (err) {
      if (this.isStaleGrantError(err) || this.isRecoverableCacheError(err)) {
        await this.purgeMsalState(pca);
        return this.loginPopup(pca, { ...request, prompt: 'login' });
      }
      throw err;
    }
  }

  private async loginPopup(
    pca: PublicClientApplication,
    request: PopupRequest
  ): Promise<string> {
    const result: AuthenticationResult = await pca.loginPopup(request);
    if (!result?.idToken) {
      throw new Error('Microsoft login did not return an idToken');
    }
    return result.idToken;
  }

  private async purgeMsalState(pca: PublicClientApplication): Promise<void> {
    try {
      await pca.clearCache();
    } catch {
    }
    this.clearAllMsalStorageKeys();
  }

  private isRecoverableCacheError(err: unknown): boolean {
    return (
      err instanceof BrowserAuthError &&
      (err.errorCode === 'interaction_in_progress' ||
        err.errorCode === 'no_token_request_cache_error' ||
        err.errorCode === 'unable_to_parse_token_request_cache_error')
    );
  }

  private isStaleGrantError(err: unknown): boolean {
    if (!(err instanceof AuthError) && !(err instanceof Error)) {
      return false;
    }
    const code = 'errorCode' in err ? String((err as AuthError).errorCode ?? '') : '';
    const message = `${code} ${err.message}`.toLowerCase();
    return (
      message.includes('invalid_grant') ||
      message.includes('aadsts70008') ||
      message.includes('bad_token') ||
      message.includes('expired due to inactivity')
    );
  }

  private clearAllMsalStorageKeys(): void {
    try {
      for (const key of Object.keys(sessionStorage)) {
        if (key.toLowerCase().includes('msal')) {
          sessionStorage.removeItem(key);
        }
      }
      for (const key of Object.keys(localStorage)) {
        if (key.toLowerCase().includes('msal')) {
          localStorage.removeItem(key);
        }
      }
    } catch {
    }
  }
}
